import React, { useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Icon } from './ui/Icon';
import { ICONS_MAP } from '../utils/constants';
import { formatCurrency, getCurrencySymbol } from '../utils/helpers';

/**
 * Customer Order History Modal
 * Shows all past orders for a customer including cancelled orders
 */
const CustomerOrderHistoryModal = ({ customer, orders, products, onClose, generalSettings, settings, onEditCustomer }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('all');

  // Get primary color from settings for dynamic theming
  const primaryColor = settings?.primaryColor || 'indigo';
  const colorMap = {
    indigo: { bg: '#4F46E5', hover: '#4338CA', text: '#4F46E5' },
    blue: { bg: '#2563EB', hover: '#1D4ED8', text: '#2563EB' },
    green: { bg: '#059669', hover: '#047857', text: '#059669' },
    purple: { bg: '#7C3AED', hover: '#6D28D9', text: '#7C3AED' },
    pink: { bg: '#DB2777', hover: '#BE185D', text: '#DB2777' },
    orange: { bg: '#EA580C', hover: '#C2410C', text: '#EA580C' },
    red: { bg: '#DC2626', hover: '#B91C1C', text: '#DC2626' },
    yellow: { bg: '#CA8A04', hover: '#A16207', text: '#CA8A04' },
    violet: { bg: '#440089', hover: '#38006E', text: '#440089' },
    teal: { bg: '#0D9488', hover: '#0F766E', text: '#0D9488' },
    cyan: { bg: '#0891B2', hover: '#0E7490', text: '#0891B2' },
    amber: { bg: '#D97706', hover: '#B45309', text: '#D97706' },
    lime: { bg: '#65A30D', hover: '#4D7C0F', text: '#65A30D' },
    fuchsia: { bg: '#C026D3', hover: '#A21CAF', text: '#C026D3' },
    rose: { bg: '#E11D48', hover: '#BE123C', text: '#E11D48' },
  };
  const themeColors = colorMap[primaryColor] || colorMap.indigo;

  // Filter orders for this customer
  const customerOrders = useMemo(() => {
    if (!customer || !orders) return [];
    return orders
      .filter(order => order.customerid === customer.id)
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first
  }, [customer, orders]);

  // Group orders by status
  const ordersByStatus = useMemo(() => {
    const grouped = {
      ongoing: [], // ordered, shipped, delivered
      completed: [], // completed
      cancelled: [], // current orders with cancelled status
      cancelHistory: [] // from customer.cancels array
    };

    customerOrders.forEach(order => {
      if (order.status === 'completed') {
        grouped.completed.push(order);
      } else if (order.status === 'cancelled') {
        grouped.cancelled.push(order);
      } else if (['ordered', 'shipped', 'delivered'].includes(order.status)) {
        grouped.ongoing.push(order);
      }
    });

    // Add cancel history from customer.cancels array
    if (customer.cancels && Array.isArray(customer.cancels)) {
      grouped.cancelHistory = customer.cancels.map(cancel => ({
        ...cancel,
        isCancelHistory: true
      }));
    }

    return grouped;
  }, [customerOrders, customer]);

  const currency = getCurrencySymbol(generalSettings?.currency || 'GBP');

  // Get product map for quick lookup
  const productMap = useMemo(() => {
    return new Map(products.map(p => [p.product, p]));
  }, [products]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Get status color from settings if available, else use defaults
  const getStatusColor = (status) => {
    if (settings?.statusColors && settings.statusColors[status]) {
      return settings.statusColors[status];
    }
    const colors = {
      ordered: 'blue',
      shipped: 'purple',
      delivered: 'yellow',
      completed: 'green',
      cancelled: 'red',
      returned: 'orange',
      deleted: 'gray'
    };
    return colors[status] || 'gray';
  };

  // Render order card
  const OrderCard = ({ order }) => {
    const statusColor = getStatusColor(order.status);
    const isCancelled = order.status === 'cancelled' || order.isCancelHistory;

    return (
      <div className={`bg-white border-2 ${isCancelled ? 'border-red-200' : 'border-gray-200'} rounded-lg p-4 mb-3 hover:shadow-md transition-shadow`}>
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-gray-900">
                #{order.isCancelHistory ? (order.orderid || 'N/A').substring(0, 8) : order.orderid.substring(0, 8)}
              </span>
              {isCancelled && (
                <Icon path={ICONS_MAP['warning']} className="w-5 h-5 text-red-600" />
              )}
            </div>
            <span className="text-sm text-gray-500">{formatDate(order.date || order.cancelDate)}</span>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${isCancelled ? 'bg-red-100 text-red-800' : `bg-${statusColor}-100 text-${statusColor}-800`} capitalize`}>
              {order.isCancelHistory ? t('cancelled') : (t(order.status) || order.status)}
            </span>
            {order.payment && (
              <span className={`px-2 py-0.5 text-xs rounded ${order.payment === 'Visa' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                {order.payment === 'Visa' ? t('visaPayment') : t('cashPayment')}
              </span>
            )}
          </div>
        </div>

        {/* Products */}
        <div className="mb-3">
          <div className="text-xs font-semibold text-gray-600 mb-1">{t('items')}:</div>
          <div className="space-y-1">
            {(order.items || []).map((item, idx) => {
              const product = productMap.get(item.pid);
              return (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-gray-700">
                    {item.pid} {item.used && <span className="text-xs text-orange-600">({t('returned')})</span>}
                  </span>
                  <span className="text-gray-600">x{item.qty}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Total */}
        <div className="flex justify-between items-center pt-3 border-t border-gray-200">
          <span className="text-sm font-semibold text-gray-700">{t('total')}:</span>
          <span className="text-lg font-bold text-gray-900">
            {formatCurrency(order.total || 0, generalSettings?.currency || 'GBP')}
          </span>
        </div>

        {/* Cancellation info */}
        {isCancelled && (
          <div className="mt-3 pt-3 border-t border-red-200 bg-red-50 -mx-4 -mb-4 px-4 py-2 rounded-b-lg">
            <div className="flex items-center gap-2 text-sm text-red-700">
              <Icon path={ICONS_MAP['cancelled']} className="w-4 h-4" />
              <span className="font-medium">
                {t('cancelled')} {formatDate(order.cancelDate || order.date)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!customer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div
          className="text-white px-6 py-4 rounded-t-lg flex justify-between items-center"
          style={{ backgroundColor: themeColors.bg }}
        >
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <h2 className="text-2xl font-bold">{customer.name}</h2>
                <p className="text-white opacity-90 text-sm">{customer.mobile}</p>
                <p className="text-white opacity-90 text-sm">{customer.city}, {customer.state}</p>
                {customer.address && <p className="text-white opacity-90 text-xs mt-1">{customer.address}</p>}
              </div>
              {onEditCustomer && (
                <button
                  onClick={() => onEditCustomer(customer)}
                  className="px-3 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all flex items-center gap-2"
                  title={t('edit')}
                >
                  <Icon path={ICONS_MAP['edit']} className="w-5 h-5" />
                  <span className="text-sm font-medium">{t('edit')}</span>
                </button>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors ml-4"
          >
            <Icon path={ICONS_MAP['close']} className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs with Stats */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-4 gap-0">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-4 font-medium text-sm transition-colors border-b-4 ${
                activeTab === 'all'
                  ? `border-${primaryColor}-600 bg-white`
                  : 'border-transparent hover:bg-gray-100'
              }`}
            >
              <div className={`text-2xl font-bold ${activeTab === 'all' ? `text-${primaryColor}-600` : 'text-gray-900'}`}>{customerOrders.length}</div>
              <div className="text-xs text-gray-600 mt-1">{t('totalOrders')}</div>
            </button>
            <button
              onClick={() => setActiveTab('ongoing')}
              className={`px-4 py-4 font-medium text-sm transition-colors border-b-4 ${
                activeTab === 'ongoing'
                  ? `border-${primaryColor}-600 bg-white`
                  : 'border-transparent hover:bg-gray-100'
              }`}
            >
              <div className={`text-2xl font-bold ${activeTab === 'ongoing' ? `text-${primaryColor}-600` : `text-${primaryColor}-600 opacity-60`}`}>{ordersByStatus.ongoing.length}</div>
              <div className="text-xs text-gray-600 mt-1">{t('ongoing')}</div>
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-4 py-4 font-medium text-sm transition-colors border-b-4 ${
                activeTab === 'completed'
                  ? 'border-green-600 bg-white'
                  : 'border-transparent hover:bg-gray-100'
              }`}
            >
              <div className="text-2xl font-bold text-green-600">{ordersByStatus.completed.length}</div>
              <div className="text-xs text-gray-600 mt-1">{t('completed')}</div>
            </button>
            <button
              onClick={() => setActiveTab('cancelled')}
              className={`px-4 py-4 font-medium text-sm transition-colors border-b-4 ${
                activeTab === 'cancelled'
                  ? 'border-red-600 bg-white'
                  : 'border-transparent hover:bg-gray-100'
              }`}
            >
              <div className="text-2xl font-bold text-red-600">{ordersByStatus.cancelHistory.length}</div>
              <div className="text-xs text-gray-600 mt-1">{t('cancelled')}</div>
            </button>
          </div>
        </div>

        {/* Orders List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {activeTab === 'all' && (
            <>
              {customerOrders.length === 0 ? (
                <div className="text-center py-12">
                  <Icon path={ICONS_MAP['ordered']} className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{t('noOrders')} </p>
                </div>
              ) : (
                customerOrders.map(order => (
                  <OrderCard key={order.orderid} order={order} />
                ))
              )}
            </>
          )}

          {activeTab === 'ongoing' && (
            <>
              {ordersByStatus.ongoing.length === 0 ? (
                <div className="text-center py-12">
                  <Icon path={ICONS_MAP['ordered']} className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{t('noOrders')} </p>
                </div>
              ) : (
                ordersByStatus.ongoing.map(order => (
                  <OrderCard key={order.orderid} order={order} />
                ))
              )}
            </>
          )}

          {activeTab === 'completed' && (
            <>
              {ordersByStatus.completed.length === 0 ? (
                <div className="text-center py-12">
                  <Icon path={ICONS_MAP['completed']} className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{t('noOrders')} </p>
                </div>
              ) : (
                ordersByStatus.completed.map(order => (
                  <OrderCard key={order.orderid} order={order} />
                ))
              )}
            </>
          )}

          {activeTab === 'cancelled' && (
            <>
              {ordersByStatus.cancelHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Icon path={ICONS_MAP['cancelled']} className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{t('noOrders')} </p>
                </div>
              ) : (
                ordersByStatus.cancelHistory.map((order, idx) => (
                  <OrderCard key={order.orderid || idx} order={order} />
                ))
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            style={{ backgroundColor: themeColors.bg }}
            className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors font-medium shadow-sm"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrderHistoryModal;

